/**
 * Shared enemy scaling logic for both the game and simulations.
 * Ensures balance testing accurately reflects actual gameplay.
 */

export type EnemyScalingMultipliers = {
  hpMultiplier: number;
  atkMultiplier: number;
  armorMultiplier: number;
};

/**
 * Calculate HP and ATK multipliers for an enemy based on floor number.
 * This is the single source of truth for enemy scaling progression.
 */
export const getEnemyScalingMultipliers = (floor: number): EnemyScalingMultipliers => {
  if (floor <= 3) {
    // Early floors: single goblin with gentle scaling
    return {
      hpMultiplier: 1 + (floor - 1) * 0.08,
      atkMultiplier: 1,
      armorMultiplier: 1,
    };
  } else if (floor <= 6) {
    // Mid floors: moderate scaling
    return {
      hpMultiplier: 1 + (floor - 1) * 0.12,
      atkMultiplier: 1 + (floor - 4) * 0.08,
      armorMultiplier: 1,
    };
  } else {
    // Late floors: challenging but not impossible
    return {
      hpMultiplier: 1.4 + (floor - 7) * 0.12,
      atkMultiplier: 1.15 + (floor - 7) * 0.08,
      armorMultiplier: 1,
    };
  }
};

/**
 * Apply scaling multipliers to an enemy instance.
 * Modifies HP, maxHP, and ATK in place.
 */
export const applyEnemyScaling = (
  enemy: { hp: number; maxHp: number; atk: number; armor?: number },
  multipliers: EnemyScalingMultipliers
): void => {
  enemy.maxHp = Math.floor(enemy.maxHp * multipliers.hpMultiplier);
  enemy.hp = enemy.maxHp;
  enemy.atk = Math.floor(enemy.atk * multipliers.atkMultiplier);
  if (typeof enemy.armor === "number") {
    enemy.armor = Math.floor(enemy.armor * multipliers.armorMultiplier);
  }
};
